import { inject, injectable } from 'inversify';
import { makeAutoObservable } from 'mobx';
import type { GridSortModel } from '@mui/x-data-grid-pro';
import { IOsiAbonentsWidgetViewModel } from '@shared/types/mobx/widgets/OsiAbonentsWidget';
import { Abonent } from '@shared/types/osi/abonents';
import { getOsiAbonents } from '@shared/api/osi/abonents/get';
import logger from 'js-logger';
import { notifications } from '@shared/utils/notifications';
import { getOsiAbonentsReport } from '@shared/api/osi/abonents/report';
import { flatComparator } from '@shared/utils/helpers/flatComparator';
import _, { sumBy } from 'lodash';
import { DictionaryModule } from '@mobx/services/dictionaries/model/DictionaryModule';
import { v4 as uuid4 } from 'uuid';
import { getBase64 } from '@shared/utils/helpers/base64Convertion';
import { loadAbonentsFromExcel } from '@shared/api/osi';
import { type OsiService, OsiServiceIdentifier } from '@shared/types/mobx/services/osi';
import { AreaTypeCodes } from '@shared/types/dictionaries';
import { MIME, downloadBase64 } from '@shared/utils/download';

@injectable()
export class OsiAbonentsWidgetViewModel implements IOsiAbonentsWidgetViewModel {
  abonents: Abonent[] = [];

  isLoading = false;

  isCreatingDialogOpen = false;

  isEditingDialogOpen = false;

  temporaryRow: any = {};

  // Вариант А: модель сортировки контролируется ViewModel, а не встроенной сортировкой DataGridPro.
  // Грид работает в режиме sortingMode="server" и лишь сообщает нам выбранную колонку/направление через
  // onSortModelChange. Порядок строк формирует геттер sortedAbonents, чтобы дети (PARKING/STORAGE) не
  // отрывались от родителей. По умолчанию [] => дефолтный порядок (родители по flat) — совпадает со старым видом.
  sortModel: GridSortModel = [];

  constructor(@inject(OsiServiceIdentifier) private osiModule: OsiService, private dictionary: DictionaryModule) {
    makeAutoObservable(this);
    void this.loadAbonents();
  }

  // Вариант А: action для обновления модели сортировки из onSortModelChange грида.
  setSortModel = (model: GridSortModel) => {
    this.sortModel = model;
  };

  private _reloadCb = async () => {
    await this.loadAbonents();
  };

  get reloadCb() {
    return this._reloadCb;
  }

  // Вариант А: компаратор РОДИТЕЛЕЙ по выбранной в гриде колонке (sortModel[0]).
  // Сравниваем по СЫРОМУ значению row[field] (а НЕ по форматированному через valueFormatter),
  // потому что грид в режиме sortingMode="server" сортировку не выполняет — её делаем мы здесь.
  // Тип сравнения зависит от поля:
  //  - числовые (id «ЛС ОСИ», square/effectiveSquare/floor/livingFact/livingJur) — как числа;
  //  - номерные строки (flat «Номер») — натурально через flatComparator;
  //  - owner — строка «Собственник»/«Арендатор» (OSI-788), сравниваем локально как строки;
  //  - прочие строки (name, idn, phone, areaTypeCode, ercAccount) — локальным сравнением строк;
  //  - неизвестное поле — безопасный фолбэк по flat.
  // Направление 'desc' инвертирует результат. Дети по этому компаратору НЕ сортируются.
  private parentComparator = (a: Abonent, b: Abonent): number => {
    const sort = this.sortModel[0];
    // Нет выбранной сортировки — текущий дефолт: родители по flat (натуральная сортировка номеров).
    if (!sort) return flatComparator(a?.flat, b?.flat);

    const { field, sort: direction } = sort;
    const dir = direction === 'desc' ? -1 : 1;

    // Числовые поля сравниваем как числа (NaN, в т.ч. строковый UUID-id временной строки, уезжает вниз).
    const numericFields = ['id', 'square', 'effectiveSquare', 'floor', 'livingFact', 'livingJur'];

    let cmp: number;
    if (field === 'flat') {
      cmp = flatComparator(a?.flat, b?.flat);
    } else if (numericFields.includes(field)) {
      const av = Number((a as any)?.[field]);
      const bv = Number((b as any)?.[field]);
      const aNaN = Number.isNaN(av);
      const bNaN = Number.isNaN(bv);
      if (aNaN && bNaN) cmp = 0;
      else if (aNaN) cmp = 1; // некорректное/временное значение всегда в конец независимо от направления
      else if (bNaN) cmp = -1;
      else cmp = av - bv;
      // Для NaN-фолбэка направление не инвертируем — иначе временные строки прыгали бы наверх при desc.
      if (aNaN || bNaN) return cmp;
    } else if (field === 'owner') {
      // OSI-788: owner — СТРОКА («Собственник»/«Арендатор») | null | ''.
      // Раньше здесь читался несуществующий .nameRu, из-за чего сортировка по колонке
      // «Владелец» всегда сравнивала пустые строки и порядок не менялся.
      // Сортируем по сырому русскому значению (не по переводу) — порядок стабилен между локалями.
      const av = (a as any)?.owner ?? '';
      const bv = (b as any)?.owner ?? '';
      cmp = String(av).localeCompare(String(bv));
    } else {
      // Прочие строковые поля (name, idn, phone, areaTypeCode, ercAccount и пр.).
      const av = (a as any)?.[field];
      const bv = (b as any)?.[field];
      if (av == null && bv == null) cmp = 0;
      else if (av == null) return 1; // пустое значение в конец независимо от направления
      else if (bv == null) return -1;
      else cmp = String(av).localeCompare(String(bv));
    }

    return cmp * dir;
  };

  get sortedAbonents() {
    // Фаза 2 + Вариант А: сортировка с группировкой parent-child.
    // РОДИТЕЛЕЙ сортируем по выбранной колонке (parentComparator), дочерние (PARKING/STORAGE)
    // ВСЕГДА идут сразу под своим родителем в стабильном порядке по flat — это и гарантирует иерархию.
    // Миграция: external удалён из БД; арендатор определяется по areaTypeCode.
    const parents = this.abonents
      .filter((a) => a.areaTypeCode !== AreaTypeCodes.ARENDATOR && !a.parentAbonentId)
      .sort((a, b) => {
        // @ts-expect-error-next-line отслеживаю isNew и не хочу расширять тип
        if (a.isNew) return 1; // новая/временная строка всегда в конец списка родителей
        // @ts-expect-error-next-line
        if (b.isNew) return -1;
        return this.parentComparator(a, b);
      });

    // Детей НЕ сортируем по выбранному полю — только стабильный порядок по номеру (flat), как раньше.
    const children = this.abonents
      .filter((a) => a.areaTypeCode !== AreaTypeCodes.ARENDATOR && !!a.parentAbonentId)
      .sort((a, b) => flatComparator(a?.flat, b?.flat));

    // Вставляем дочерние записи сразу после родительских
    const result: typeof this.abonents = [];
    for (const parent of parents) {
      result.push(parent);
      const parentChildren = children.filter((c) => c.parentAbonentId === parent.id);
      result.push(...parentChildren);
    }
    // Дочерние без найденного родителя (orphans) — в конец
    const orphans = children.filter((c) => !parents.some((p) => p.id === c.parentAbonentId));
    result.push(...orphans);

    const externalAbonents = this.abonents
      .filter((abonent) => abonent.areaTypeCode === AreaTypeCodes.ARENDATOR)
      .sort((a, b) => flatComparator(a?.flat, b?.flat));

    return [...result, ...externalAbonents];
  }

  get totalAbonents() {
    return this.abonents.length;
  }

  // Счётчик помещений ОСИ (план 2.8): соответствует Osi.ApartCount —
  // активные абоненты без parent_abonent_id. Дочерние PARKING/STORAGE
  // не учитываются (у них parent установлен), ARENDATOR — учитывается
  // (parent IS NULL). Используется в надписи "Всего" над DataGrid.
  get apartCount() {
    return this.abonents.filter((a) => !a.parentAbonentId).length;
  }

  get totalSquare() {
    return sumBy(this.abonents, 'square').toFixed(2);
  }

  get totalParkingPlaces() {
    return sumBy(this.abonents, 'parkingPlaces');
  }

  get osiId() {
    return this.osiModule.osiId;
  }

  get areaTypes() {
    return this.dictionary.areaTypes;
  }

  downloadReport = async () => {
    try {
      if (!this.osiModule.osiId || !this.osiModule.osiInfo?.name) throw new Error('osiId is undefined');
      this.isLoading = true;
      const data = await getOsiAbonentsReport(this.osiModule.osiId);
      const house = this.osiModule.osiInfo?.address
        ?.split(',')
        .slice(-1)
        .pop()
        ?.replace('д.', '')
        .replace('дом', '')
        .trim();
      const osiName = this.osiModule.osiInfo?.name.startsWith(this.osiModule.osiInfo?.unionTypeRu ?? '')
        ? this.osiModule.osiInfo?.name
        : `${this.osiModule.osiInfo?.unionTypeRu} ${this.osiModule.osiInfo?.name}`;
      // iOS/WebKit: data:-URL не скачивается по атрибуту download — переведено на blob: через downloadBase64
      downloadBase64(
        data as unknown as string,
        MIME.XLSX,
        `Помещения(квартиры)_${osiName}_дом_${house}_${new Date().toISOString().substring(0, 10)}.xlsx`
      );
    } catch (e) {
      logger.error(e);
      notifications.error();
    } finally {
      this.isLoading = false;
    }
  };

  startCreatingAbonent = () => {
    this.isCreatingDialogOpen = true;
  };

  stopCreatingAbonent = () => {
    this.isCreatingDialogOpen = false;
  };

  startEditingAbonent = () => {
    this.isEditingDialogOpen = true;
  };

  stopEditingAbonent = () => {
    this.isEditingDialogOpen = false;
  };

  // OSI-806: чекбокс «Свернуть дочерние помещения» рендерится по флагу osiInfo.hasChildAbonents.
  // Критерий считает бэк (активный абонент с parentAbonentId != null и типом PARKING/STORAGE) и
  // отдаёт готовый флаг в GET /api/Osi/{id}. Список помещений после сохранения обновляется локально,
  // а osiInfo в singleton-сервисе OsiService — нет, поэтому без этого сайд-эффекта чекбокс появлялся
  // (или исчезал) только после перезахода в дом.
  //
  // Почему loadOsiInfo(), а не refreshOsi(): refreshOsi дополнительно дёргает loadOsiMenus() —
  // запрос неподписанных актов и пересчёт isLockedMenus, на которые состав помещений не влияет.
  // Точечный loadOsiInfo — канон проекта, см. widgets/osi/info/model/viewModel.ts (после OsiUpdate).
  //
  // Ошибку намеренно гасим в логгер: помещение уже сохранено на бэке, и упавшее по сети обновление
  // osiInfo (loadOsiInfo делает throw e) не должно превращаться в notifications.error поверх успешного
  // сохранения. Флаг просто останется прежним до следующей загрузки osiInfo.
  private refreshOsiInfoSilently = async () => {
    try {
      await this.osiModule.loadOsiInfo();
    } catch (e) {
      logger.error('OSI-806: не удалось обновить osiInfo после изменения состава помещений', e);
    }
  };

  // OSI-806: единственное место, где сформулировано условие «правка МОЖЕТ изменить hasChildAbonents».
  // Условное, а не безусловное обновление — чтобы обычное редактирование площади/имени/телефона
  // не тянуло за собой лишний запрос к API.
  private isChildAbonentsAffected = (
    before: { areaTypeCode?: string | null; parentAbonentId?: number | null } | null,
    after: { areaTypeCode?: string | null; parentAbonentId?: number | null }
  ) => {
    const isChildAreaType = (code?: string | null) => code === AreaTypeCodes.PARKING || code === AreaTypeCodes.STORAGE;

    // Тип «дочерний» с ЛЮБОЙ стороны правки: паркинг→жилое снимает признак ровно так же,
    // как жилое→паркинг его ставит, поэтому смотрим и старое, и новое значение.
    if (isChildAreaType(before?.areaTypeCode) || isChildAreaType(after?.areaTypeCode)) return true;

    // Смена привязки к родителю, включая обнуление (undefined и null считаем одним и тем же «нет родителя»).
    return (before?.parentAbonentId ?? null) !== (after?.parentAbonentId ?? null);
  };

  updateAbonent = (updated: Abonent) => {
    const oldAbonent = this.abonents.find((abonent) => abonent.id === updated.id);

    if (!oldAbonent) return;

    // OSI-806: снимок «дочерних» полей снимаем ДО Object.assign — после мержа старые значения
    // уже недоступны, а без них не отличить «паркинг→жилое» от обычной правки.
    const isAffected = this.isChildAbonentsAffected(
      { areaTypeCode: oldAbonent.areaTypeCode, parentAbonentId: oldAbonent.parentAbonentId },
      updated
    );

    Object.assign(oldAbonent, updated);

    // OSI-806: сайд-эффект намеренно fire-and-forget. updateAbonent синхронна и вызывается из
    // обработчиков компонента, обёрнутых в try/catch с notifications.error — если ждать и пробрасывать
    // ошибку сюда, сетевой сбой обновления osiInfo показался бы пользователю как ошибка сохранения.
    if (isAffected) void this.refreshOsiInfoSilently();
  };

  // OSI-806: симметричная updateAbonent точка добавления созданного помещения в локальный список.
  // Компонент больше не делает vm.abonents.push(...) напрямую — иначе условие «затронуты ли дочерние
  // помещения» пришлось бы дублировать в UI рядом с каждым вызовом API.
  pushAbonent = (created: Abonent) => {
    this.abonents.push(created);

    // before = null: у только что созданной записи прошлого состояния нет, признак может только появиться
    // (создали PARKING/STORAGE либо сразу указали parentAbonentId).
    if (this.isChildAbonentsAffected(null, created)) void this.refreshOsiInfoSilently();
  };

  addAbonent = () => {
    const id = uuid4();

    const flat = this.calculateNextFlat();

    // Миграция: поле external удалено из модели Abonent — временная строка не нуждается в нём.
    this.abonents.push({ id, flat, isNew: true, ...this.temporaryRow });
    return id;
  };

  calculateNextFlat = () => {
    const existingFlats = this.abonents
      .filter((abonent) => abonent.areaTypeCode !== AreaTypeCodes.ARENDATOR)
      .map((abonent) => abonent.flat)
      .sort((a, b) => flatComparator(a, b));
    const lastFlat = parseInt(existingFlats.slice(-1)[0], 10);

    if (!lastFlat) return (this.totalAbonents + 1).toString();

    return (lastFlat + 1).toString();
  };

  cancelCreating = () => {
    // @ts-expect-error-next-line отслеживаю isNew и не хочу расширять тип
    _.remove(this.abonents, (abonent) => abonent.isNew);
  };

  saveTemporaryRow = (temporaryRow: any) => {
    const { id, flat, ...tempRow } = temporaryRow;
    this.temporaryRow = tempRow;
  };

  clearTemporaryRow = () => {
    this.temporaryRow = {};
  };

  importAbonents = async (file: File) => {
    try {
      if (!this.osiId) throw new Error('OsiId is undefined');
      this.isLoading = true;
      const b64 = await getBase64(file);
      await loadAbonentsFromExcel(this.osiId, b64.bytes);
      await this.loadAbonents();
      // OSI-806: импорт из Excel может и создать паркинги/кладовки, и снять привязки к родителю,
      // а что именно поменялось — из ответа не видно. Поэтому здесь обновляем osiInfo БЕЗУСЛОВНО,
      // без проверки isChildAbonentsAffected. Ошибка внутри погашена — на успех импорта не влияет.
      await this.refreshOsiInfoSilently();
      notifications.success();
    } catch (e) {
      logger.error(e);
      notifications.error(e);
    } finally {
      this.isLoading = false;
    }
  };

  private loadAbonents = async () => {
    try {
      if (!this.osiModule.osiId) throw new Error('osiId is undefined');
      this.isLoading = true;
      this.abonents = await getOsiAbonents(this.osiModule.osiId);
    } catch (e) {
      logger.error(e);
      notifications.error();
    } finally {
      this.isLoading = false;
    }
  };
}
